import { Add, Close } from '@mui/icons-material';
import { Button, Divider, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useMutateCreateApplicationRegister } from '/@/hooks/application-register';
import { useCropProtectionProducts } from '/@/hooks/catalog/use-crop-protection-products';
import { useMutateCreateCropMonitoringNote } from '/@/hooks/crop-monitoring-notes';
import { useNitrogenSources } from '/@/hooks/nitrogen-smart';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import useIsMobile from '/@/utils/hooks/is-mobile';
import {
    applicationInvalidFields,
    applicationPrefillEdited,
    applicationProductsKey,
    errorDetail,
    flowEventProps,
    trackFormAbandoned,
    useFieldRegistryFlow,
    useFormExitTracking,
} from './flow-tracking';
import type { RegistryAddStep } from './types';

const UNITS = ['lts/ha', 'cc/ha', 'kg/ha', 'gr/ha', 'ml/ha'];
const OTHER = 'other';
/** Not a real crop-protection product type — selects the nitrogen-source product list instead. */
const FERTILIZANTES = 'fertilizantes';
type TypeValue = number | typeof FERTILIZANTES | '';

interface TypeOption {
    id: number | typeof FERTILIZANTES;
    name: string;
}

/** Fixed display order for "Tipo de aplicación" — anything not listed here (incl. a literal
 *  "Otros" catalog type) sorts after Fertilizante, in whatever order it was fetched in. */
const TYPE_ORDER = ['herbicida', 'insecticida', 'fungicida', 'tratamiento de semilla'];
const normalizeTypeName = (name: string) =>
    name
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

interface ProductRow {
    productId: number | typeof OTHER | '';
    productName: string;
    dosis: string;
    unit: string;
    unitOther: string;
}

const emptyRow = (): ProductRow => ({ productId: '', productName: '', dosis: '', unit: '', unitOther: '' });

interface NitrogenSourceOption {
    id: number;
    name: string;
}

const rowIsValid = (row: ProductRow) =>
    (row.productId === OTHER ? row.productName.trim() !== '' : row.productId !== '') &&
    row.dosis.trim() !== '' &&
    (row.unit === _('Otro') ? row.unitOther.trim() !== '' : row.unit !== '');

const CreateRegistryApplication = ({
    open,
    onClose,
    onSuccess,
    setStep,
    fieldId,
    initialProductNames,
}: {
    open: boolean;
    onClose: () => void;
    /** With the ids of the applications created, one per product. */
    onSuccess: (createdIds?: number[]) => void;
    setStep?: (step: RegistryAddStep) => void;
    fieldId?: number;
    /** Prefill for the "Registrar lo aplicado" flow — seeds product rows (and the type) from a
     *  recommendation's recommended products, matched to the catalog by name. */
    initialProductNames?: string[];
}) => {
    const { products, types } = useCropProtectionProducts();
    const { data: nitrogenSources } = useNitrogenSources() as { data: NitrogenSourceOption[] | undefined };
    const createApplication = useMutateCreateApplicationRegister();
    const createNote = useMutateCreateCropMonitoringNote();

    const [applicationDate, setApplicationDate] = useState<Dayjs | null>(null);
    const [typeId, setTypeId] = useState<TypeValue>('');
    const [note, setNote] = useState('');
    const [rows, setRows] = useState<ProductRow[]>([emptyRow()]);

    const isFertilizantes = typeId === FERTILIZANTES;
    // Errors show once the user tries to register; before that the form starts clean.
    const [showErrors, setShowErrors] = useState(false);
    // What the recommendation brought once it landed in the form, to tell whether it was edited.
    const prefill = useRef<{ typeId: TypeValue; productsKey: string } | undefined>(undefined);

    // Seed product rows + type from the recommendation once the catalog is available. The
    // date, dose and unit are left for the user to complete ("con o sin cambios").
    const prefillApplied = useRef(false);
    // Rows already saved by an earlier, partly failed submit, so a retry doesn't create them again.
    // Keyed by the row object: editing a row replaces it, so an edited row is saved anew.
    const savedRows = useRef(new WeakMap<ProductRow, { batch: string; id: number | undefined }>());
    const isMobile = useIsMobile();
    useEffect(() => {
        if (!open) {
            prefillApplied.current = false;
            return;
        }
        if (prefillApplied.current || !initialProductNames?.length || !products.length) return;
        prefillApplied.current = true;

        const normalize = (name: string) => name.trim().toLowerCase();
        const prefilledRows: ProductRow[] = initialProductNames.map((name) => {
            const match = products.find((product) => normalize(product.name) === normalize(name));
            return match
                ? { productId: match.id, productName: '', dosis: '', unit: '', unitOther: '' }
                : { productId: OTHER, productName: name, dosis: '', unit: '', unitOther: '' };
        });
        setRows(prefilledRows);
        const firstMatch = products.find((product) =>
            initialProductNames.some((name) => normalize(product.name) === normalize(name))
        );
        const prefilledType: TypeValue = firstMatch?.type?.id ?? '';
        if (prefilledType !== '') setTypeId(prefilledType);
        prefill.current = { typeId: prefilledType, productsKey: applicationProductsKey(prefilledRows) };
    }, [open, initialProductNames, products]);

    const typeProducts =
        typeId === '' || isFertilizantes ? products : products.filter((product) => product.type?.id === typeId);
    const nitrogenSourceOptions = nitrogenSources ?? [];

    // Herbicida, Insecticida, Fungicida, Tratamiento de semilla, Fertilizante, then anything
    // else (e.g. a catalog "Otros" type) in whatever order it was fetched in.
    const orderedTypeOptions = useMemo<TypeOption[]>(() => {
        const fertilizanteOption: TypeOption = { id: FERTILIZANTES, name: _('Fertilizante') };
        const rank = (name: string) => {
            const index = TYPE_ORDER.indexOf(normalizeTypeName(name));
            return index === -1 ? TYPE_ORDER.length + 1 : index;
        };
        return [...types, fertilizanteOption]
            .map((type, index) => ({ type, index }))
            .sort((a, b) => {
                const rankA = a.type.id === FERTILIZANTES ? TYPE_ORDER.length : rank(a.type.name);
                const rankB = b.type.id === FERTILIZANTES ? TYPE_ORDER.length : rank(b.type.name);
                return rankA !== rankB ? rankA - rankB : a.index - b.index;
            })
            .map(({ type }) => type);
    }, [types]);

    const updateRow = (index: number, patch: Partial<ProductRow>) =>
        setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    const removeRow = (index: number) => setRows((current) => current.filter((_row, i) => i !== index));

    const isValid =
        Boolean(applicationDate) && typeId !== '' && rows.length > 0 && rows.every(rowIsValid) && Boolean(fieldId);
    const invalidFields = applicationInvalidFields({ applicationDate, typeId, rows }, _('Otro'));
    const dateError = showErrors && !applicationDate;
    const typeError = showErrors && typeId === '';

    // Only inside a flow: "Agregar registro", a claim, the visit report or a recommendation card.
    // Editing a registry from its card has none.
    const flow = useFieldRegistryFlow();
    const formTouched =
        Boolean(applicationDate) ||
        typeId !== '' ||
        note.trim() !== '' ||
        rows.some((row) => row.productId !== '' || row.dosis.trim() !== '' || row.unit !== '');
    const { end, finish } = useFormExitTracking((exitMethod) => {
        if (flow) trackFormAbandoned(flow, 'application', exitMethod, formTouched);
    });
    const handleCancel = () => {
        end('cancel');
        onClose();
    };
    const handleModalClose = () => {
        end('close');
        onClose();
    };

    const handleSubmit = async () => {
        if (createApplication.isMutating || !fieldId) return;
        if (!isValid || !applicationDate) {
            setShowErrors(true);
            if (flow)
                mpTrackEvent('Field Registry Submit Attempted', {
                    ...flowEventProps(flow),
                    registry_type: 'application',
                    invalid_fields: invalidFields,
                });
            return;
        }

        const applicationDateStr = applicationDate.format('YYYY-MM-DD');
        // A row saved with another date or type no longer matches what the user is submitting
        const batch = `${applicationDateStr}|${typeId}`;
        let firstId: number | undefined;
        const createdIds: number[] = [];

        try {
            for (const row of rows) {
                const saved = savedRows.current.get(row);
                if (saved?.batch === batch) {
                    if (firstId === undefined) firstId = saved.id;
                    if (saved.id !== undefined) createdIds.push(saved.id);
                    continue;
                }
                const isOther = row.productId === OTHER;
                const created = await createApplication.trigger(
                    isFertilizantes
                        ? {
                              field: fieldId,
                              applicationDate: applicationDateStr,
                              type: null,
                              nitrogenSource: row.productId as number,
                              cropProtectionProduct: null,
                              cropProtectionProductName:
                                  nitrogenSourceOptions.find((source) => source.id === row.productId)?.name ?? '',
                              dosis: Number(row.dosis),
                              unity: row.unit === _('Otro') ? row.unitOther.trim() : row.unit,
                          }
                        : {
                              field: fieldId,
                              applicationDate: applicationDateStr,
                              type: typeId as number,
                              cropProtectionProduct: isOther ? null : (row.productId as number),
                              cropProtectionProductName: isOther
                                  ? row.productName.trim()
                                  : (products.find((p) => p.id === row.productId)?.name ?? ''),
                              dosis: Number(row.dosis),
                              unity: row.unit === _('Otro') ? row.unitOther.trim() : row.unit,
                          }
                );
                savedRows.current.set(row, { batch, id: created?.id });
                if (firstId === undefined) firstId = created?.id;
                if (created?.id !== undefined) createdIds.push(created.id);
            }

            if (note.trim() && firstId) {
                await createNote.trigger({ field: fieldId, applicationRegister: firstId, content: note.trim() });
            }
        } catch (error) {
            if (flow)
                mpTrackEvent('Field Registry Save Failed', {
                    ...flowEventProps(flow),
                    registry_type: 'application',
                    origin: flow.origin,
                    error_detail: errorDetail(error),
                    // One call per product: some may already be saved
                    partial: firstId !== undefined,
                });
            // Keep the modal (and everything typed in it) open so the user can retry.
            enqueueSnackbar(_('No se pudo guardar la aplicación. Intentá nuevamente.'), { variant: 'error' });
            return;
        }

        finish();
        // Once per submit, however many products (and calls) it took
        if (flow)
            mpTrackEvent('Field Registry Saved', {
                ...flowEventProps(flow),
                registry_type: 'application',
                origin: flow.origin,
                entry_source: flow.entrySource,
                products_count: rows.length,
                used_other_product: rows.some((row) => row.productId === OTHER),
                ...(flow.origin === 'recommendation' && prefill.current
                    ? {
                          prefill_edited: applicationPrefillEdited(prefill.current, {
                              typeId,
                              productsKey: applicationProductsKey(rows),
                          }),
                      }
                    : {}),
            });
        onSuccess(createdIds);
    };

    return (
        <ModalV2
            open={open}
            onClose={handleModalClose}
            goBack={
                setStep
                    ? () => {
                          // Back to the type picker: the attempt goes on there.
                          finish();
                          setStep('select');
                      }
                    : undefined
            }
            title={_('Registrar una aplicación')}
            bodyProps={{ alignItems: 'stretch', textAlign: 'left', spacing: 3 }}
            actionsProps={{ justifyContent: 'flex-end' }}
            actions={
                isMobile ? (
                    <Stack direction='row' spacing={1} sx={{ width: '100%' }}>
                        <Button
                            variant='outlined'
                            color='secondary'
                            onClick={handleCancel}
                            sx={{ flex: 1, height: 'auto' }}
                        >
                            {_('Cancelar')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            disabled={!fieldId || createApplication.isMutating}
                            onClick={handleSubmit}
                            sx={{ flex: 1 }}
                        >
                            {_('Registrar en el seguimiento')}
                        </Button>
                    </Stack>
                ) : (
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={!fieldId || createApplication.isMutating}
                        onClick={handleSubmit}
                    >
                        {_('Registrar en el seguimiento')}
                    </Button>
                )
            }
        >
            <Stack spacing={3}>
                <Stack spacing={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Fecha de aplicación')}
                    </Typography>
                    <MuiDatePicker
                        fullWidth
                        value={applicationDate}
                        onChange={(value) => setApplicationDate(value)}
                        slotProps={{
                            textField: {
                                placeholder: _('Seleccionar una fecha'),
                                error: dateError,
                                helperText: dateError ? _('Campo requerido') : undefined,
                            },
                        }}
                        maxDate={dayjs()}
                    />
                </Stack>

                <Stack spacing={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Tipo de aplicación')}
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        value={typeId}
                        onChange={(event) => {
                            const value = event.target.value;
                            setTypeId(value === FERTILIZANTES ? FERTILIZANTES : Number(value));
                            // Product options depend on the type — clear any chosen products.
                            setRows([emptyRow()]);
                        }}
                        SelectProps={{ displayEmpty: true }}
                        error={typeError}
                        helperText={typeError ? _('Campo requerido') : undefined}
                    >
                        <MenuItem value='' disabled>
                            {_('Elegir tipo')}
                        </MenuItem>
                        {orderedTypeOptions.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                {rows.map((row, index) => {
                    const productError = showErrors && typeId !== '' && row.productId === '';
                    const productNameError = showErrors && row.productId === OTHER && row.productName.trim() === '';
                    const dosisError = showErrors && row.dosis.trim() === '';
                    const unitError = showErrors && row.unit === '';
                    const unitOtherError = showErrors && row.unit === _('Otro') && row.unitOther.trim() === '';
                    return (
                        // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and have no stable id
                        <Stack key={index} spacing={2}>
                            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                <Typography variant='overline' color='primary'>
                                    {`${_('Producto')} ${index + 1}`}
                                </Typography>
                                {/* At least one product is required, so the lone row can't be removed. */}
                                {rows.length > 1 && (
                                    <IconButton
                                        size='small'
                                        aria-label={_('Eliminar producto')}
                                        onClick={() => removeRow(index)}
                                        sx={{
                                            width: '16px !important',
                                            height: '16px !important',
                                            p: '8px !important',
                                            '& .MuiSvgIcon-root': {
                                                width: '16px !important',
                                                height: '16px !important',
                                            },
                                        }}
                                    >
                                        <Close />
                                    </IconButton>
                                )}
                            </Stack>
                            <TextField
                                select
                                fullWidth
                                value={row.productId}
                                disabled={typeId === ''}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    updateRow(index, {
                                        productId: value === OTHER ? OTHER : Number(value),
                                        productName: '',
                                    });
                                }}
                                SelectProps={{ displayEmpty: true }}
                                error={productError}
                                helperText={productError ? _('Campo requerido') : undefined}
                            >
                                <MenuItem value='' disabled>
                                    {isFertilizantes ? _('Elegir fuente de nitrógeno') : _('Elegir producto')}
                                </MenuItem>
                                {isFertilizantes
                                    ? nitrogenSourceOptions.map((source) => (
                                          <MenuItem key={source.id} value={source.id}>
                                              {source.name}
                                          </MenuItem>
                                      ))
                                    : typeProducts.map((product) => (
                                          <MenuItem key={product.id} value={product.id}>
                                              {product.name}
                                          </MenuItem>
                                      ))}
                                {!isFertilizantes && <MenuItem value={OTHER}>{_('Otro')}</MenuItem>}
                            </TextField>

                            {!isFertilizantes && row.productId === OTHER && (
                                <TextField
                                    fullWidth
                                    placeholder={_('Nombre del producto')}
                                    value={row.productName}
                                    onChange={(event) => updateRow(index, { productName: event.target.value })}
                                    error={productNameError}
                                    helperText={productNameError ? _('Campo requerido') : undefined}
                                />
                            )}

                            <Stack direction='row' spacing={2}>
                                <TextField
                                    fullWidth
                                    type='number'
                                    placeholder={_('Dosis')}
                                    value={row.dosis}
                                    onChange={(event) => updateRow(index, { dosis: event.target.value })}
                                    error={dosisError}
                                    helperText={dosisError ? _('Campo requerido') : undefined}
                                />
                                <TextField
                                    select
                                    fullWidth
                                    value={row.unit}
                                    onChange={(event) => updateRow(index, { unit: event.target.value })}
                                    SelectProps={{ displayEmpty: true }}
                                    error={unitError}
                                    helperText={unitError ? _('Campo requerido') : undefined}
                                >
                                    <MenuItem value='' disabled>
                                        {_('Unidad')}
                                    </MenuItem>
                                    {UNITS.map((unit) => (
                                        <MenuItem key={unit} value={unit}>
                                            {unit}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value={_('Otro')}>{_('Otro')}</MenuItem>
                                </TextField>
                            </Stack>

                            {row.unit === _('Otro') && (
                                <TextField
                                    fullWidth
                                    placeholder={_('Unidad')}
                                    value={row.unitOther}
                                    onChange={(event) => updateRow(index, { unitOther: event.target.value })}
                                    error={unitOtherError}
                                    helperText={unitOtherError ? _('Campo requerido') : undefined}
                                />
                            )}

                            {index === rows.length - 1 && (
                                <>
                                    <Divider />
                                    <Button
                                        variant='text'
                                        color='primary'
                                        startIcon={<Add />}
                                        sx={{ alignSelf: 'flex-start' }}
                                        onClick={() => setRows((current) => [...current, emptyRow()])}
                                    >
                                        {_('Agregar otro producto')}
                                    </Button>
                                </>
                            )}
                        </Stack>
                    );
                })}

                <Stack spacing={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Nota')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder={_('Escribir una observación')}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                    />
                </Stack>
            </Stack>
        </ModalV2>
    );
};

export default CreateRegistryApplication;
